/**
 * Fix: cart drawer loses its inner scroll after an in-drawer cart change
 * (quantity change, upsell add, free-gift auto-add, discount).
 *
 * Why it happens:
 * The cart drawer is a modal <dialog>. Its scroll only works while the dialog is
 * in the top-layer :modal state, because the scroll height comes from
 *   .cart-drawer__dialog:modal { max-height: 100dvh; overflow-y: hidden }
 * combined with
 *   .cart-drawer__content { height: calc(100% - var(--header-height)); overflow-y: auto }
 *
 * Every in-drawer cart change re-renders (morphs) the cart/header section. The
 * server markup contains the dialog in its closed, non-modal state, so the morph
 * can knock the live dialog out of :modal and/or leave the scroll container's
 * overflow stale. Result: the content is no longer viewport-bounded, there is
 * nothing left to scroll, and the drawer feels "locked".
 *
 * The previous version only listened for 'cart:update' / 'discount:update', so
 * adding an upsell (which fires 'cart:add') was never repaired, and the qty-change
 * repair was timing-fragile. This version is event-agnostic: a MutationObserver on
 * the drawer re-asserts the scroll state after ANY re-render while the drawer is
 * open, and the cart events are still bound as a backup.
 */
(() => {
  const SUMMARY_THRESHOLD = 0.5;

  function getComponent() {
    return document.querySelector('cart-drawer-component');
  }

  /** Find the drawer dialog defensively: class first, any dialog as fallback. */
  function getDialog(component) {
    if (!component) return null;
    return (
      component.querySelector('dialog.cart-drawer__dialog') ||
      component.querySelector('dialog')
    );
  }

  function isOpen(dialog) {
    return !!dialog && dialog.open;
  }

  /** Mirror of the theme's sticky-summary calc so the layout mode matches the new cart size. */
  function updateStickyState(dialog) {
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    if (!drawerHeight) return;

    const ratio = summary.getBoundingClientRect().height / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > SUMMARY_THRESHOLD ? 'false' : 'true');
  }

  /** Re-establish the scrollable region after the subtree was morphed. */
  function reassertScroll(dialog) {
    if (!isOpen(dialog)) return;

    const content = dialog.querySelector('.cart-drawer__content');
    const scrollTop = content ? content.scrollTop : 0;

    // If the morph demoted the dialog out of the top layer, the :modal CSS that
    // bounds the scroll height stops applying. Re-promote it the way close+reopen
    // would, with the open animation suppressed so it does not re-slide.
    let modal = true;
    try {
      modal = dialog.matches(':modal');
    } catch (e) {
      modal = true;
    }

    if (!modal) {
      const prevAnimation = dialog.style.animation;
      try {
        dialog.style.animation = 'none';
        dialog.close();
        dialog.showModal();
      } catch (e) {
        /* no-op */
      } finally {
        requestAnimationFrame(() => {
          dialog.style.animation = prevAnimation;
        });
      }
    }

    // Force the browser to recompute the scroll area. Mutating an open modal
    // dialog's subtree can leave the overflow container stuck until a reflow.
    if (content) {
      const prev = content.style.overflowY;
      content.style.overflowY = 'hidden';
      void content.offsetHeight; // reflow
      content.style.overflowY = prev; // restore stylesheet value (auto)
      content.scrollTop = scrollTop;
    }

    updateStickyState(dialog);
  }

  let scheduled = false;
  function schedule() {
    if (scheduled) return;
    scheduled = true;
    // Wait two frames: run after the morph and after layout has settled.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        scheduled = false;
        const dialog = getDialog(getComponent());
        if (isOpen(dialog)) reassertScroll(dialog);
      });
    });
  }

  // Primary: react to ANY re-render inside the drawer while it is open.
  // childList + subtree only (not attributes), so our own style/attribute writes
  // do not retrigger the observer.
  function attachObserver() {
    const component = getComponent();
    if (!component) return false;
    const observer = new MutationObserver(() => {
      const dialog = getDialog(component);
      if (isOpen(dialog)) schedule();
    });
    observer.observe(component, { childList: true, subtree: true });
    return true;
  }

  // Backup: cart events. Bound as strings so this works without module imports.
  document.addEventListener('cart:update', schedule);
  document.addEventListener('cart:add', schedule);
  document.addEventListener('discount:update', schedule);

  if (!attachObserver()) {
    // Component not in the DOM yet (e.g. late hydration); retry once.
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', attachObserver, { once: true });
    } else {
      requestAnimationFrame(attachObserver);
    }
  }
})();